import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import { useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import React, { useMemo, useRef, useState } from 'react';

import { PickTask } from '../../../@types/picktask.type';
import { ColumnSelectionItem } from '../../../components/templates/ColumnSelection';
import { formatSendBy, formatTs, SendByWarning } from '../pickTaskHelpers';

// 'in-progress' -> 'In-progress' etc. so statuses read professionally.
const capitalize = (s?: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '-');

export interface RepSort {
  field: string;
  sort: 'asc' | 'desc';
}

interface PtColumnDef {
  label: string;
  width?: number;
  minWidth?: number;
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  render?: (row: PickTask) => React.ReactNode;
}

const COLUMN_DEFS: Record<string, PtColumnDef> = {
  id: { label: 'ID', width: 80, sortable: true },
  status: {
    label: 'Status',
    width: 130,
    sortable: true,
    render: (r) => capitalize(r.status),
  },
  allocatedTo: {
    label: 'Allocated To',
    width: 170,
    sortable: true,
    render: (r) => r.allocatedToUserName ?? r.allocatedToUserId ?? '-',
  },
  from: {
    label: 'From Location',
    width: 200,
    sortable: true,
    render: (r) => r.fromLocationName ?? r.fromLocationNodeId ?? '-',
  },
  to: {
    label: 'To Location',
    width: 200,
    sortable: true,
    render: (r) => r.toLocationName ?? r.toLocationNodeId ?? '-',
  },
  lines: { label: 'Lines', width: 80, align: 'center', render: (r) => r.history?.length ?? 0 },
  progress: {
    label: 'Shipped / Req',
    width: 130,
    align: 'center',
    render: (r) => {
      const h = r.history ?? [];
      const shipped = h.reduce((a, x) => a + (x.qtyShipped ?? 0), 0);
      const req = h.reduce((a, x) => a + (x.qtyRequired ?? 0), 0);
      return `${shipped} / ${req}`;
    },
  },
  sendBy: {
    label: 'Send By',
    width: 180,
    sortable: true,
    // Due date + a "due soon/today/overdue" reminder chip for open tasks.
    render: (r) => (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
        {formatSendBy(r.sendBy)}
        <SendByWarning task={r} />
      </Box>
    ),
  },
  createdAt: { label: 'Created', width: 170, sortable: true, render: (r) => formatTs(r.createdAt) },
  createdBy: {
    label: 'Created By',
    width: 160,
    sortable: true,
    render: (r) => r.createdByName ?? r.createdBy ?? '-',
  },
  completedAt: {
    label: 'Completed At',
    width: 170,
    sortable: true,
    render: (r) => formatTs(r.completedAt),
  },
  cancelledBy: {
    label: 'Cancelled By',
    width: 160,
    sortable: true,
    render: (r) => r.cancelledByName ?? r.cancelledBy ?? '-',
  },
  cancelledAt: {
    label: 'Cancelled At',
    width: 170,
    sortable: true,
    render: (r) => formatTs(r.cancelledAt),
  },
  fromLocationType: {
    label: 'From Location Type',
    width: 160,
    sortable: true,
    render: (r) => r.fromLocationTypeName ?? r.fromLocationTypeCode ?? '-',
  },
  toLocationType: {
    label: 'To Location Type',
    width: 160,
    sortable: true,
    render: (r) => r.toLocationTypeName ?? r.toLocationTypeCode ?? '-',
  },
  pickTaskDays: {
    label: 'Pick Task Days',
    width: 130,
    align: 'center',
    // Days the task has been open: created -> completed/cancelled, or now if still open.
    render: (r) => {
      // Zone-less backend timestamps are UTC; parse them consistently.
      const ms = (s?: string | null) => {
        if (!s) return NaN;
        const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(s);
        return new Date(hasZone ? s : `${s}Z`).getTime();
      };
      const start = ms(r.createdAt);
      if (isNaN(start)) return '-';
      const endStr = r.completedAt || r.cancelledAt;
      const end = endStr ? ms(endStr) : Date.now();
      return Math.max(0, Math.floor((end - start) / 86400000));
    },
  },
};

interface PtColumn extends PtColumnDef {
  field: string;
}

const HEADER_CELL_SX = {
  fontSize: '10px',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
  fontWeight: 600,
  color: 'text.secondary',
  whiteSpace: 'nowrap' as const,
  py: 0.75,
  px: 1,
  borderBottom: '2px solid',
  borderColor: 'v4.border',
  bgcolor: 'v4.tableEven',
} as const;

const CELL_SX = {
  fontSize: '11px',
  // PickTask rows are text-only (no logos/chips/multi-line lists like the
  // Services and Replenishment tables), so the shared py: 0.25 collapses them
  // to a cramped minimum. A little more vertical padding gives the rows the
  // same breathing room those tables get from their taller cell content.
  py: 0.75,
  px: 1,
  borderBottom: '1px solid',
  borderColor: 'v4.border',
} as const;

const dropIndicatorSx = (side: 'left' | 'right' | null, accent: string) => {
  if (side !== 'left' && side !== 'right') return undefined;
  const pseudo = side === 'left' ? '&::before' : '&::after';
  return {
    [pseudo]: {
      content: '""',
      position: 'absolute',
      [side]: 0,
      top: 4,
      bottom: 4,
      width: '2px',
      bgcolor: accent,
      borderRadius: '1px',
    },
  };
};

const SortableHeaderCell: React.FC<{
  col: PtColumn;
  sortModel?: RepSort;
  onSortChange: (field: string) => void;
  dropIndicator?: 'left' | 'right' | null;
}> = ({ col, sortModel, onSortChange, dropIndicator }) => {
  const theme = useTheme();
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({ id: col.field });
  const isSorted = sortModel?.field === col.field;
  return (
    <TableCell
      ref={setNodeRef}
      align={col.align}
      sx={{
        ...HEADER_CELL_SX,
        position: 'relative',
        opacity: isDragging ? 0.4 : 1,
        color: isSorted ? theme.palette.v4.accent : 'text.secondary',
        ...(col.width ? { width: col.width } : {}),
        ...(col.minWidth ? { minWidth: col.minWidth } : {}),
        ...dropIndicatorSx(dropIndicator ?? null, theme.palette.v4.accent),
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        {/* Dedicated drag handle: carries the dnd-kit listeners so reordering
            (pointer and keyboard) is separate from the sort control below. */}
        <Box
          component="button"
          type="button"
          aria-label={`Reorder ${col.label} column`}
          {...attributes}
          {...listeners}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            background: 'none',
            border: 'none',
            p: 0,
            color: 'inherit',
            cursor: isDragging ? 'grabbing' : 'grab',
            touchAction: 'none',
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 14 }} />
        </Box>
        <Box
          component={col.sortable ? 'button' : 'span'}
          type={col.sortable ? 'button' : undefined}
          onClick={col.sortable ? () => onSortChange(col.field) : undefined}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            background: 'none',
            border: 'none',
            p: 0,
            font: 'inherit',
            color: 'inherit',
            letterSpacing: 'inherit',
            textTransform: 'inherit',
            fontWeight: 'inherit',
            cursor: col.sortable ? 'pointer' : 'inherit',
          }}
        >
          {col.label}
          {isSorted &&
            (sortModel?.sort === 'asc' ? (
              <ArrowUpwardIcon sx={{ fontSize: 13 }} />
            ) : (
              <ArrowDownwardIcon sx={{ fontSize: 13 }} />
            ))}
        </Box>
      </Box>
    </TableCell>
  );
};

export interface PickTaskTableProps {
  rows: PickTask[];
  sortModel?: RepSort;
  columnSelection: ColumnSelectionItem[];
  selectedIds: number[];
  onSortChange: (field: string) => void;
  onReorder: (activeField: string, overField: string) => void;
  onToggleRow: (id: number) => void;
  onToggleAll: (checked: boolean) => void;
  onSelectRange: (ids: number[]) => void;
  onRowClick: (id: number) => void;
}

export const PickTaskTable: React.FC<PickTaskTableProps> = ({
  rows,
  sortModel,
  columnSelection,
  selectedIds,
  onSortChange,
  onReorder,
  onToggleRow,
  onToggleAll,
  onSelectRange,
  onRowClick,
}) => {
  const theme = useTheme();
  const lastIndexRef = useRef<number | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const pageRowIds = rows.map((r) => Number(r.id));
  const allSelected = pageRowIds.length > 0 && pageRowIds.every((id) => selectedIds.includes(id));
  const someSelected = pageRowIds.some((id) => selectedIds.includes(id));

  const handleRowClick = (e: React.MouseEvent, idx: number, id: number) => {
    if (e.shiftKey && lastIndexRef.current !== null) {
      const [start, end] = [lastIndexRef.current, idx].sort((a, b) => a - b);
      onSelectRange(rows.slice(start, end + 1).map((r) => Number(r.id)));
    } else {
      onToggleRow(id);
      lastIndexRef.current = idx;
    }
  };

  const columns: PtColumn[] = useMemo(() => {
    const out: PtColumn[] = [];
    columnSelection.forEach((group) =>
      group.value.forEach((item) => {
        if (item.value && COLUMN_DEFS[item.field]) {
          out.push({
            field: item.field,
            ...COLUMN_DEFS[item.field],
            label: item.title || COLUMN_DEFS[item.field].label,
          });
        }
      })
    );
    return out;
  }, [columnSelection]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));
  const handleDragOver = (e: DragOverEvent) => setOverId(e.over ? String(e.over.id) : null);
  const handleDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    setOverId(null);
    const { active, over } = e;
    if (over && active.id !== over.id) onReorder(String(active.id), String(over.id));
  };

  const dropSide: 'left' | 'right' | null = (() => {
    if (!activeId || !overId || activeId === overId) return null;
    const activeIdx = columns.findIndex((c) => c.field === activeId);
    const overIdx = columns.findIndex((c) => c.field === overId);
    return activeIdx < overIdx ? 'right' : 'left';
  })();

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'v4.border',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <TableContainer sx={{ maxHeight: 'calc(100vh - 320px)' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={HEADER_CELL_SX}>
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onChange={(e) => onToggleAll(e.target.checked)}
                  slotProps={{ input: { 'aria-label': 'Select all rows' } }}
                />
              </TableCell>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToHorizontalAxis]}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={columns.map((c) => c.field)}
                  strategy={horizontalListSortingStrategy}
                >
                  {columns.map((col) => (
                    <SortableHeaderCell
                      key={col.field}
                      col={col}
                      sortModel={sortModel}
                      onSortChange={onSortChange}
                      dropIndicator={col.field === overId ? dropSide : null}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 1}
                  sx={{ ...CELL_SX, textAlign: 'center', py: 3 }}
                >
                  <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                    No results
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, idx) => {
                const id = Number(row.id);
                const selected = selectedIds.includes(id);
                const even = idx % 2 === 0;
                const rowBg = selected
                  ? theme.palette.v4.tableSelected
                  : even
                    ? theme.palette.v4.tableEven
                    : theme.palette.background.paper;
                const hoverBg = selected
                  ? theme.palette.v4.tableSelectedHover
                  : theme.palette.v4.tableHover;
                return (
                  <TableRow
                    key={id || idx}
                    onClick={(e) => handleRowClick(e, idx, id)}
                    sx={{
                      bgcolor: rowBg,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: hoverBg },
                      transition: 'background-color 0.15s ease',
                    }}
                  >
                    <TableCell padding="checkbox" sx={CELL_SX}>
                      <Checkbox
                        size="small"
                        checked={selected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => onToggleRow(id)}
                        slotProps={{ input: { 'aria-label': `Select pick task ${id}` } }}
                      />
                    </TableCell>
                    {columns.map((col) => (
                      <TableCell
                        key={col.field}
                        align={col.align}
                        sx={{
                          ...CELL_SX,
                          ...(col.width ? { width: col.width } : {}),
                          ...(col.minWidth ? { minWidth: col.minWidth } : {}),
                        }}
                      >
                        {col.field === 'id' ? (
                          // A real button: a span with onClick cannot be reached or
                          // activated by keyboard, which made the list impossible to
                          // open a task from without a mouse.
                          <Box
                            component="button"
                            type="button"
                            aria-label={`Open pick task ${row.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              onRowClick(id);
                            }}
                            sx={{
                              color: 'v4.accent',
                              fontWeight: 600,
                              cursor: 'pointer',
                              background: 'none',
                              border: 'none',
                              p: 0,
                              font: 'inherit',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            {row.id}
                          </Box>
                        ) : col.render ? (
                          col.render(row)
                        ) : (
                          String((row as unknown as Record<string, unknown>)[col.field] ?? '')
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
